import { useEffect, useState, useMemo, useCallback, useRef } from "react"
import { useNavigate } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { useLessonStore, getPreviewDeck } from "@/store/lessonStore"
import { SettingsPanel } from "./SettingsPanel"
import { ExportMenu } from "@/components/microcourse/export/ExportMenu"
import { SlideCanvas } from "@/components/microcourse/SlideCanvas"
import { PlaybackBar } from "@/components/microcourse/playback/PlaybackBar"
import { useAutoplay } from "@/components/microcourse/playback/useAutoplay"
import { injectThemeCSS } from "@/components/microcourse/themes/themeRegistry"
import { updateSlideElement } from "@/api/lesson"
import { reportMicroQuiz } from "@/api/mistakes"
import type { MicroElement, ExcalidrawElement } from "@ai-tutor/shared"
import type { QuizSubmitResult } from "@/components/microcourse/elements/ElementRenderer"
import type { MicrocourseProject } from "@/types/lesson"

interface LessonWorkbenchProps {
  lessonId: string
}

export default function LessonWorkbench({ lessonId }: LessonWorkbenchProps) {
  const navigate = useNavigate()
  const {
    lesson,
    loading,
    error,
    localSettings,
    settingsDirty,
    ttsLoading,
    loadLesson,
    clearLesson,
    updateLocalSettings,
    saveSettings,
    resetSettings,
    generateTTS,
    setDeck,
  } = useLessonStore()

  const [insertingWhiteboard, setInsertingWhiteboard] = useState(false)
  const [quizTip, setQuizTip] = useState<string | null>(null)
  const quizTipTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    loadLesson(lessonId)
    return () => clearLesson()
  }, [lessonId])

  // Get preview deck with local settings applied
  const deck = useMemo(
    () => getPreviewDeck(lesson, localSettings),
    [lesson, localSettings]
  ) as MicrocourseProject | undefined

  const slides = useMemo(
    () => (deck ? [...deck.slides].sort((a, b) => a.order - b.order) : []),
    [deck]
  )

  // Autoplay engine（fragment 级播放引擎，作为唯一位置来源）
  const autoplay = useAutoplay({ slides })

  const activeSlideIndex = autoplay.currentSlideIndex
  const activeSlide = slides[activeSlideIndex]
  const activeFragmentIndex = autoplay.fragmentIndex

  const isPlaying = autoplay.status === 'playing' || autoplay.status === 'paused'

  // 插入白板：向当前 slide 添加一个空白 excalidraw 元素
  const handleInsertExcalidraw = useCallback(async () => {
    if (!activeSlide) return
    setInsertingWhiteboard(true)
    try {
      const newElement: ExcalidrawElement = {
        id: `excalidraw-${Date.now()}`,
        type: 'excalidraw',
        content: JSON.stringify({
          type: 'excalidraw',
          version: 2,
          elements: [],
          appState: { viewBackgroundColor: '#ffffff' },
        }),
        title: '手绘白板',
        height: 420,
        viewMode: false,
      }
      const { deck: updatedDeck } = await updateSlideElement({
        lessonId,
        slideId: activeSlide.id,
        action: 'add',
        element: newElement,
      })
      setDeck(updatedDeck)
    } catch (e) {
      console.error('插入白板失败:', e)
    } finally {
      setInsertingWhiteboard(false)
    }
  }, [activeSlide, lessonId, setDeck])

  // 白板编辑回写：持久化到后端并用权威响应同步本地 deck
  // （Excalidraw 已挂载且仅 mount 时读 initialData，setDeck 不会导致画布重置）
  const handleElementChange = useCallback(
    async (element: MicroElement) => {
      if (!activeSlide) return
      try {
        const { deck: updatedDeck } = await updateSlideElement({
          lessonId,
          slideId: activeSlide.id,
          action: 'update',
          element,
        })
        setDeck(updatedDeck)
      } catch (e) {
        console.error('保存白板失败:', e)
      }
    },
    [activeSlide, lessonId, setDeck]
  )

  // 课堂练习答题上报：答错时同步到错题本
  const handleQuizResult = useCallback(
    (result: QuizSubmitResult) => {
      reportMicroQuiz({
        lessonId,
        elementId: result.elementId,
        slideId: activeSlide?.id,
        lessonTitle: deck?.title,
        quiz: result.quiz,
        userAnswer: result.userAnswer,
        isCorrect: result.isCorrect,
      })
        .then(() => {
          if (result.isCorrect) return
          setQuizTip('错题已同步到错题本')
          if (quizTipTimerRef.current) clearTimeout(quizTipTimerRef.current)
          quizTipTimerRef.current = setTimeout(() => setQuizTip(null), 3000)
        })
        .catch((e) => {
          console.error('课堂练习上报失败:', e)
        })
    },
    [lessonId, activeSlide?.id, deck?.title]
  )

  // Unmount 时清理提示定时器
  useEffect(() => {
    return () => {
      if (quizTipTimerRef.current) clearTimeout(quizTipTimerRef.current)
    }
  }, [])

  // Inject theme CSS based on local settings
  useEffect(() => {
    if (deck?.theme) {
      return injectThemeCSS(deck.theme)
    }
  }, [deck?.theme])

  // Inject custom color variables (CSS custom properties, no !important)
  useEffect(() => {
    if (!deck?.customColors) return
    const { primary, accent, bg } = deck.customColors
    const style = document.createElement('style')
    style.setAttribute('data-microcourse-custom-colors', '')
    style.textContent = `
      .reveal {
        --mc-primary: ${primary};
        --mc-accent: ${accent};
        --mc-bg: ${bg};
      }
    `
    document.head.appendChild(style)
    return () => style.remove()
  }, [deck?.customColors])

  // Inject slide transition animations (once)
  useEffect(() => {
    const style = document.createElement('style')
    style.setAttribute('data-microcourse-transitions', '')
    style.textContent = `
      @keyframes mc-slide-in { from { transform: translateX(3%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
      @keyframes mc-fade-in { from { opacity: 0; } to { opacity: 1; } }
      @keyframes mc-convex-in { from { transform: rotateY(15deg) translateX(3%); opacity: 0; } to { transform: rotateY(0) translateX(0); opacity: 1; } }
      @keyframes mc-concave-in { from { transform: rotateY(-15deg) translateX(-3%); opacity: 0; } to { transform: rotateY(0) translateX(0); opacity: 1; } }
      @keyframes mc-zoom-in { from { transform: scale(0.92); opacity: 0; } to { transform: scale(1); opacity: 1; } }
      .mc-transition { animation-duration: 0.5s; animation-fill-mode: both; }
      .mc-transition-slide { animation-name: mc-slide-in; }
      .mc-transition-fade { animation-name: mc-fade-in; }
      .mc-transition-convex { animation-name: mc-convex-in; }
      .mc-transition-concave { animation-name: mc-concave-in; }
      .mc-transition-zoom { animation-name: mc-zoom-in; }
      .mc-transition-none { animation: none !important; }
    `
    document.head.appendChild(style)
    return () => style.remove()
  }, [])

  return (
    <div className="flex flex-col h-screen">
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 py-2 border-b bg-background">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate("/teacher")}>
            ← 返回
          </Button>
          <h1 className="text-lg font-semibold truncate max-w-[300px]">
            {deck?.title ?? lesson?.topic ?? "微课"}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          {deck && activeSlide && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleInsertExcalidraw}
              disabled={insertingWhiteboard}
            >
              {insertingWhiteboard ? "插入中…" : "✏️ 插入白板"}
            </Button>
          )}
          {deck && (
            <ExportMenu lessonId={lessonId} project={deck} />
          )}
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex min-h-0">
        {/* Left: Settings panel */}
        <div className="w-[280px] border-r flex flex-col min-h-0 bg-muted/30">
          {localSettings && (
            <SettingsPanel
              settings={localSettings}
              dirty={settingsDirty}
              saving={loading}
              ttsLoading={ttsLoading}
              onSettingsChange={updateLocalSettings}
              onSave={saveSettings}
              onReset={resetSettings}
              onGenerateTTS={generateTTS}
            />
          )}
        </div>

        {/* Right: Preview + Playback */}
        <div className="flex-1 flex flex-col min-h-0">
          {loading && !deck && (
            <div className="flex-1 flex items-center justify-center">
              <div className="animate-spin w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full" />
            </div>
          )}

          {error && !deck && (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center space-y-3">
                <div className="text-red-500">{error}</div>
                <Button onClick={() => navigate("/teacher")}>返回列表</Button>
              </div>
            </div>
          )}

          {deck && slides.length > 0 && (
            <>
              {/* Slide thumbnail strip */}
              <div className="flex gap-2 px-4 py-3 overflow-x-auto border-b bg-muted/20">
                {slides.map((slide, i) => (
                  <button
                    key={slide.id}
                    className={`flex-shrink-0 w-28 p-2 rounded-md border text-left transition-all ${
                      activeSlideIndex === i
                        ? "border-blue-500 bg-blue-50 dark:bg-blue-950/30 shadow-sm"
                        : "border-gray-200 bg-white dark:bg-gray-800 hover:border-gray-400"
                    }`}
                    onClick={() => autoplay.goToSlide(i)}
                  >
                    <div className="text-[10px] text-muted-foreground mb-0.5">
                      {i + 1}
                      {slide.elements.some((e) => e.type === 'quiz') && " 📝"}
                      {slide.segments.length > 0 && " 🔊"}
                    </div>
                    <div className="text-xs font-medium truncate">{slide.title}</div>
                  </button>
                ))}
              </div>

              {/* Slide canvas */}
              <div
                key={`slide-${activeSlideIndex}-${deck.transition}`}
                className={`reveal relative flex-1 min-h-0 mc-transition mc-transition-${deck.transition ?? 'slide'}`}
                style={{
                  '--mc-primary': deck.customColors?.primary,
                  '--mc-accent': deck.customColors?.accent,
                  '--mc-bg': deck.customColors?.bg,
                  fontFamily: deck.fontFamily,
                  zoom: String(deck.fontScale ?? 1),
                } as React.CSSProperties}
              >
                {activeSlide && (
                  <SlideCanvas
                    slide={activeSlide}
                    fragmentIndex={activeFragmentIndex}
                    isSelected={false}
                    editable={!isPlaying}
                    onElementChange={handleElementChange}
                    onQuizResult={handleQuizResult}
                  />
                )}

                {/* 错题同步提示 */}
                {quizTip && (
                  <div className="absolute top-3 left-1/2 -translate-x-1/2 z-20 px-3 py-1.5 rounded-full bg-blue-600/90 text-white text-xs shadow-lg">
                    ✓ {quizTip}
                  </div>
                )}
              </div>

              {/* Playback controls */}
              <PlaybackBar
                status={autoplay.status}
                currentSlideIndex={activeSlideIndex}
                totalSlides={slides.length}
                currentSegIndex={autoplay.currentSegIndex}
                totalSegments={activeSlide?.segments?.length ?? 0}
                statusText={autoplay.statusText}
                speaking={autoplay.speaking}
                onPlay={() => autoplay.start(activeSlideIndex)}
                onPause={autoplay.pause}
                onResume={autoplay.resume}
                onPrev={autoplay.stepPrev}
                onNext={autoplay.stepNext}
                prevDisabled={!autoplay.canPrev}
                nextDisabled={!autoplay.canNext}
              />
            </>
          )}
        </div>
      </div>
    </div>
  )
}
